import { Link } from "@tanstack/react-router";
import { Facehash } from "facehash";
import { AnimatePresence, m } from "framer-motion";
import {
	ChevronDown,
	CircleHelp,
	CreditCard,
	FileClock,
	FileText,
	Home,
	KeyRound,
	LogOut,
	MessageSquare,
	Settings,
	Sparkles,
	User,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalizeName } from "../../lib/format";

interface UserMenuProps {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
		role?: string | null;
	};
	isImpersonating: boolean;
	showUpgradeButton: boolean;
	isSigningOut: boolean;
	onUpgrade: () => void;
	onSignOut: () => void;
	menuSide?: "top" | "right" | "bottom" | "left";
	menuAlign?: "start" | "center" | "end";
	menuSideOffset?: number;
	triggerClassName?: string;
	wrapperClassName?: string;
	showUserName?: boolean;
	showChevron?: boolean;
	userNameClassName?: string;
	membershipLabel?: string | null;
}

export function UserMenu({
	user,
	isImpersonating,
	showUpgradeButton,
	isSigningOut,
	onUpgrade,
	onSignOut,
	menuSide,
	menuAlign = "end",
	menuSideOffset = 8,
	triggerClassName,
	wrapperClassName,
	showUserName = true,
	showChevron = true,
	userNameClassName,
	membershipLabel,
}: UserMenuProps) {
	const userAvatarAlt = capitalizeName(user.name) || user.email || "User";

	return (
		<div className={wrapperClassName ?? "flex items-center gap-1.5 p-1"}>
			{/* Upgrade Button */}
			<AnimatePresence>
				{showUpgradeButton && (
					<m.button
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						type="button"
						onClick={onUpgrade}
						className="hidden sm:flex h-9 items-center gap-2 px-3 rounded-lg text-xs font-medium border border-border text-foreground hover:bg-muted"
					>
						<Sparkles className="w-3 h-3 text-primary" />
						Upgrade
					</m.button>
				)}
			</AnimatePresence>

			{/* User Dropdown */}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={
							triggerClassName ??
							"flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg border border-border hover:bg-muted transition-colors duration-200 group cursor-pointer"
						}
					>
						<div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center overflow-hidden">
							{user.image ? (
								<img
									src={user.image}
									alt={userAvatarAlt}
									className="h-full w-full object-cover"
									loading="lazy"
									referrerPolicy="no-referrer"
								/>
							) : (
								<Facehash name={user.email || user.id} size={32} />
							)}
						</div>
						{showUserName && (
							<span className="flex min-w-0 flex-1 flex-col items-start leading-tight">
								<span
									className={
										userNameClassName ??
										"hidden lg:block text-xs font-medium text-foreground max-w-[120px] truncate"
									}
								>
									{capitalizeName(user.name) || "User"}
								</span>
								{membershipLabel && (
									<span className="text-[10px] font-medium text-muted-foreground truncate max-w-full">
										{membershipLabel}
									</span>
								)}
							</span>
						)}
						{showChevron && (
							<ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
						)}
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					side={menuSide}
					align={menuAlign}
					sideOffset={menuSideOffset}
					className="w-72 p-2 bg-card border-border shadow-lg rounded-lg"
				>
					{/* User info header */}
					<div className="px-2 py-2">
						<div className="flex items-center gap-3 rounded-lg border border-border bg-muted p-2">
							<div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
								{user.image ? (
									<img
										src={user.image}
										alt={userAvatarAlt}
										className="h-full w-full object-cover"
										loading="lazy"
										referrerPolicy="no-referrer"
									/>
								) : (
									<Facehash name={user.email || user.id} size={40} />
								)}
							</div>
							<div className="flex flex-col min-w-0 flex-1">
								<span className="text-sm font-medium text-foreground truncate">
									{capitalizeName(user.name) || "User"}
								</span>
								<span className="text-xs text-muted-foreground/90 truncate">
									{user.email}
								</span>
							</div>
						</div>
					</div>

					<div className="h-px bg-border/60 my-1" />

					{/* Navigation items */}
					<div className="space-y-0.5">
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<Home className="w-4 h-4" />
								<span className="font-medium">Chat</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/settings"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<Settings className="w-4 h-4" />
								<span className="font-medium">Settings</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/billing"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<CreditCard className="w-4 h-4" />
								<span className="font-medium">Billing</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/api-keys"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<KeyRound className="w-4 h-4" />
								<span className="font-medium">API Keys & MCP</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/docs"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<CircleHelp className="w-4 h-4" />
								<span className="font-medium">Docs</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/privacy"
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
							>
								<FileText className="w-4 h-4" />
								<span className="font-medium">Privacy Policy</span>
							</Link>
						</DropdownMenuItem>
						{/* Mobile upgrade button */}
						{showUpgradeButton && (
							<DropdownMenuItem
								onClick={onUpgrade}
								className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-primary hover:bg-muted/60 transition-colors sm:hidden"
							>
								<Sparkles className="w-4 h-4" />
								<span className="font-medium">Upgrade</span>
							</DropdownMenuItem>
						)}
					</div>

					{/* Admin section */}
					{user.role === "admin" && !isImpersonating && (
						<>
							<div className="h-px bg-border/60 my-1" />
							<div className="px-3 py-1.5">
								<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
									Admin
								</span>
							</div>
							<div className="space-y-0.5">
								<DropdownMenuItem asChild>
									<Link
										to="/admin/users"
										className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
									>
										<User className="w-4 h-4" />
										<span className="font-medium">Users</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/admin/feedback"
										className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
									>
										<MessageSquare className="w-4 h-4" />
										<span className="font-medium">Feedback</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/admin/activity-logs"
										className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
									>
										<FileClock className="w-4 h-4" />
										<span className="font-medium">Activity logs</span>
									</Link>
								</DropdownMenuItem>
							</div>
						</>
					)}

					{/* Sign out */}
					<div className="h-px bg-border/60 my-1" />
					<DropdownMenuItem
						disabled={isSigningOut}
						onClick={onSignOut}
						className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
					>
						<LogOut className="w-4 h-4" />
						<span className="font-medium">Sign Out</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
